/* ============================================
   AUDIO PLAYER COMPONENT
   The Creative Orchestra - GEMINI Framework
   Audio player with animated equalizer bars
   ============================================ */

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, Headphones } from 'lucide-react';
import { useTheme } from '@context/ThemeContext';

/**
 * Animated equalizer bars that respond to audio playback
 */
const EqualizerBars = ({ isPlaying, accentColor, accentRgb, barCount = 24 }) => {
  return (
    <div className="flex items-end justify-center gap-[2px] h-10">
      {Array.from({ length: barCount }).map((_, i) => {
        const delay = i * 0.05;
        const baseHeight = 15 + Math.sin(i * 0.8) * 10;

        return (
          <motion.div
            key={i}
            className="w-[3px] rounded-full origin-bottom"
            style={{
              background: `linear-gradient(to top, rgba(${accentRgb}, 0.6), rgba(${accentRgb}, 1))`,
            }}
            animate={
              isPlaying
                ? {
                    height: [
                      `${baseHeight}%`,
                      `${Math.min(100, baseHeight + Math.random() * 70)}%`,
                      `${baseHeight + Math.random() * 30}%`,
                      `${Math.min(100, baseHeight + Math.random() * 60)}%`,
                      `${baseHeight}%`,
                    ],
                  }
                : { height: `${baseHeight * 0.4}%` }
            }
            transition={
              isPlaying
                ? {
                    duration: 0.8 + Math.random() * 0.6,
                    repeat: Infinity,
                    repeatType: 'loop',
                    delay: delay,
                    ease: 'easeInOut',
                  }
                : { duration: 0.5, ease: 'easeOut' }
            }
          />
        );
      })}
    </div>
  );
};

/**
 * Format seconds into mm:ss
 */
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Audio Player Component
 * Themed audio player with animated equalizer visualization
 *
 * @param {Object} props
 * @param {Object} props.audio - { file: string, title: string }
 * @param {string} props.workName - Name of the work
 */
const AudioPlayer = ({ audio, workName, className = '' }) => {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const { activeTheme } = useTheme();

  const accentColor = activeTheme?.colors?.accent || '#818CF8';
  const accentRgb = activeTheme?.cssVariables?.['--theme-accent-rgb'] || '129, 140, 248';

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  const handleProgressClick = useCallback((e) => {
    const audio = audioRef.current;
    const bar = progressRef.current;
    if (!audio || !bar) return;

    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * audio.duration;
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoaded(true);
    };
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <section className={`py-12 px-4 ${className}`}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {/* Background glow */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              background: `radial-gradient(ellipse at center, rgba(${accentRgb}, 0.5) 0%, transparent 70%)`,
            }}
          />

          <div className="relative z-10 p-6 md:p-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div
                className="p-2.5 rounded-xl"
                style={{ background: `rgba(${accentRgb}, 0.15)` }}
              >
                <Headphones className="w-5 h-5" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Listen to this Work
                </h3>
                <p className="text-sm text-gray-400">{audio.title}</p>
              </div>
            </div>

            {/* Equalizer visualization */}
            <div className="mb-6 px-4">
              <EqualizerBars
                isPlaying={isPlaying}
                accentColor={accentColor}
                accentRgb={accentRgb}
              />
            </div>

            {/* Controls */}
            <div className="flex items-center gap-4">
              {/* Play/Pause */}
              <motion.button
                onClick={togglePlay}
                className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-colors"
                style={{
                  background: `rgba(${accentRgb}, 0.2)`,
                  border: `1px solid rgba(${accentRgb}, 0.3)`,
                }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
              >
                <AnimatePresence mode="wait">
                  {isPlaying ? (
                    <motion.div
                      key="pause"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                    >
                      <Pause className="w-5 h-5 text-white" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="play"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                    >
                      <Play className="w-5 h-5 text-white ml-0.5" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>

              {/* Progress bar */}
              <div className="flex-1 flex items-center gap-3">
                <span className="text-xs text-gray-400 w-10 text-right font-mono">
                  {formatTime(currentTime)}
                </span>

                <div
                  ref={progressRef}
                  className="flex-1 h-2 rounded-full bg-white/10 cursor-pointer relative group"
                  onClick={handleProgressClick}
                >
                  <motion.div
                    className="h-full rounded-full relative"
                    style={{
                      width: `${progress}%`,
                      background: `linear-gradient(to right, rgba(${accentRgb}, 0.7), ${accentColor})`,
                    }}
                  >
                    <div
                      className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full 
                        opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      style={{ background: accentColor }}
                    />
                  </motion.div>
                </div>

                <span className="text-xs text-gray-400 w-10 font-mono">
                  {formatTime(duration)}
                </span>
              </div>

              {/* Mute toggle */}
              <motion.button
                onClick={toggleMute}
                className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-white transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                {isMuted ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </motion.button>
            </div>
          </div>

          {/* Hidden audio element */}
          <audio ref={audioRef} src={audio.file} preload="metadata" />
        </motion.div>
      </div>
    </section>
  );
};

export default AudioPlayer;
